/**
 * The weights of the shipment: a row per item it carries, then the total the label is quoted on.
 * An item's weight is asked for only when the aggregator takes per-item weights; otherwise the
 * row may stay empty, as the prefetch may send it.
 */
import { observer } from "mobx-react";
import FontBody2 from "../../../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../../../../../components/inputs/input/input";
import { IValidationGroup } from "../../../../../../../../../hooks/validation-group/validation-group-hook";
import { IShippingDetailsItem } from "../../../../../../rma-types";
import ParcelStore from "../../../../../../stores/rma-action-store/parcel-store";
import { greaterThanZero } from "../parcel-details-validation";

/** The total is what the parcel weighed, so it is allowed to differ from the items inside it. */
const TOTAL_WEIGHT_TOOLTIP = "The shipment weight overrides the weights of the items in it.";

/** The product name, or the SKU of a line the aggregator was sent no line item info for. */
const nameOf = (item: IShippingDetailsItem): string => item.productName ?? item.sku;

interface IProps {
    parcel: ParcelStore;
    validationGroup: IValidationGroup;
}

const ShipmentWeight: React.FC<IProps> = observer(({ parcel, validationGroup }) => (
    <>
        {parcel.items.map((item, index) => (
            <FormFieldLine
                key={item.product_case_id}
                label={`Item ${index + 1} (${nameOf(item)})`}
                required={parcel.itemWeightsRequired}
            >
                <Input
                    type="number"
                    min={0}
                    temp
                    value={item.weight ?? null}
                    onChange={(weight) => parcel.setItemWeight(item.product_case_id, weight)}
                    required={parcel.itemWeightsRequired}
                    validationGroup={validationGroup}
                    validationCallback={() => greaterThanZero(item.weight ?? null)}
                />
            </FormFieldLine>
        ))}

        <FormFieldLine
            label="Total shipment weight"
            tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{TOTAL_WEIGHT_TOOLTIP}</FontBody2>}
            required
        >
            <Input
                type="number"
                min={0}
                temp
                value={parcel.totalWeight}
                onChange={parcel.setTotalWeight}
                required
                validationGroup={validationGroup}
                validationCallback={() => greaterThanZero(parcel.totalWeight)}
            />
        </FormFieldLine>
    </>
));

export default ShipmentWeight;
